import { config } from "../config/env";
import { isExpectedGraphQLError, Sentry } from "../config/sentry";

export interface ErrorContext {
	operation: string;
	organizationId?: string;
	metadata?: Record<string, unknown>;
	// Bypass the "expected error" filter (network/fetch noise) for user-initiated
	// critical operations — e.g. a bulk import save — that must never be dropped.
	// Pass an already-sanitized error message here so Sentry's own `ignoreErrors`
	// (which substring-matches "Failed to fetch"/"Load failed") can't filter it.
	forceReport?: boolean;
}

function isExpectedError(error: unknown): boolean {
	if (error instanceof Error) {
		const lowerMessage = error.message.toLowerCase();
		if (
			lowerMessage.includes("not authenticated") ||
			lowerMessage.includes("user not found") ||
			lowerMessage.includes("access denied") ||
			lowerMessage.includes("failed to fetch") ||
			lowerMessage.includes("network error")
		) {
			return true;
		}
	}

	if (
		error &&
		typeof error === "object" &&
		"message" in error &&
		typeof (error as { message: unknown }).message === "string"
	) {
		const graphQLError = error as {
			message: string;
			extensions?: Record<string, unknown>;
		};
		return isExpectedGraphQLError(graphQLError);
	}

	return false;
}

export function captureError(error: unknown, context: ErrorContext): void {
	const errorObj = error instanceof Error ? error : new Error(String(error));

	console.error(`[${context.operation}]`, errorObj.message, {
		organizationId: context.organizationId,
		...context.metadata,
	});

	if (config.isProduction && (context.forceReport || !isExpectedError(error))) {
		Sentry.captureException(errorObj, {
			tags: {
				operation: context.operation,
				organizationId: context.organizationId,
			},
			extra: context.metadata,
		});
	}
}

export function captureWarning(message: string, context: ErrorContext): void {
	console.warn(`[${context.operation}]`, message, {
		organizationId: context.organizationId,
		...context.metadata,
	});

	if (config.isProduction) {
		Sentry.captureMessage(message, {
			level: "warning",
			tags: {
				operation: context.operation,
				organizationId: context.organizationId,
			},
			extra: context.metadata,
		});
	}
}

export function logDev(message: string, data?: unknown): void {
	if (!config.isProduction) {
		if (data !== undefined) {
			console.log(`[DEV] ${message}`, data);
		} else {
			console.log(`[DEV] ${message}`);
		}
	}
}
